import React, { memo } from 'react';
import BonusWidget from './BonusWidget';
import RecentReceiptsWidget from './RecentReceiptsWidget';
import MessagesWidget from './MessagesWidget';
import PosTasksBox from './PosTasksBox';

const documentTypes = [
  { value: 'paragon', label: 'Paragon', icon: '🧾', color: 'var(--success)' },
  { value: 'paragon_nip', label: 'Paragon NIP', icon: '🧾', color: 'var(--orange)' },
  { value: 'faktura_imienna', label: 'Faktura im.', icon: '📋', color: 'var(--info)' },
  { value: 'faktura_firma', label: 'Faktura firma', icon: '🏢', color: 'var(--purple)' },
];

const StatsBar = ({
  stats,
  salesTarget,
  currentShift,
  loading,
  onOpenShift,
  onCloseShift,
  bonusRefreshTrigger,
  documentType,
  onDocumentTypeChange,
  locationId,
  onClickRecentReceipt,
  blockFakturaFirma = false,
  userLogin
}) => {
  // Blokada administracyjna: gdy wlaczona, "faktura_firma" jest calkowicie ukryta w POS.
  const visibleDocTypes = blockFakturaFirma
    ? documentTypes.filter(t => t.value !== 'faktura_firma')
    : documentTypes;
  const activeDocType = documentTypes.find(d => d.value === documentType) || documentTypes[0];

  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        marginBottom: "0.75rem",
        flexWrap: "wrap",
        alignItems: "stretch",
      }}
    >
      {/* A) Dokument + Sprzedaz dzisiaj - jedna kolumna */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "190px" }}>
        {/* Typ dokumentu — dropdown (tylko w zakladce POS) */}
        {onDocumentTypeChange && (
          <div
            style={{
              backgroundColor: "var(--card-bg)",
              borderLeft: `3px solid ${activeDocType.color}`,
              borderRadius: "4px",
              padding: "0.5rem 0.75rem",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px", fontWeight: "600" }}>
              📄 Dokument
            </div>
            <select
              value={documentType}
              onChange={(e) => onDocumentTypeChange(e.target.value)}
              style={{
                padding: "6px 10px",
                fontSize: "12px",
                fontWeight: "600",
                border: `1px solid ${activeDocType.color}`,
                borderRadius: "6px",
                backgroundColor: "var(--card-bg)",
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              {visibleDocTypes.map(t => (
                <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Sprzedaz dzisiaj - polaczone Paragony + Obrot + Srednia */}
        <div
          style={{
            backgroundColor: "var(--card-bg)",
            borderLeft: "3px solid var(--success)",
            borderRadius: "4px",
            padding: "0.4rem 0.6rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <div style={{ fontSize: "10px", fontWeight: 600, color: "var(--text-muted)", marginBottom: "4px" }}>
            Sprzedaz dzisiaj
          </div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "space-between" }}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--primary)", lineHeight: 1.1 }}>
                {stats.receiptsToday}
              </div>
              <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Paragony</div>
            </div>
            <div style={{ width: "1px", backgroundColor: "var(--border)" }} />
            <div style={{ textAlign: "center", flex: 1.3 }}>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--success)", lineHeight: 1.1 }}>
                {stats.dailyRevenue.toFixed(2)}
              </div>
              <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Obrot (zl)</div>
            </div>
            <div style={{ width: "1px", backgroundColor: "var(--border)" }} />
            <div style={{ textAlign: "center", flex: 1.2 }}>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--info)", lineHeight: 1.1 }}>
                {stats.averageReceipt.toFixed(2)}
              </div>
              <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Srednia (zl)</div>
            </div>
          </div>
        </div>
      </div>

      {/* B) Ostatnie paragony - status fiskalizacji */}
      <RecentReceiptsWidget
        refreshTrigger={bonusRefreshTrigger}
        locationId={locationId}
        onClickReceipt={onClickRecentReceipt}
      />

      {/* C) Cel miesieczny + Premia (Premia pod celem) */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "180px" }}>
        <div
          style={{
            backgroundColor: "var(--card-bg)",
            borderLeft: "3px solid var(--warning)",
            borderRadius: "4px",
            padding: "0.5rem 0.75rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            flex: 1,
          }}
        >
          <div
            style={{ fontSize: "11px", fontWeight: "600", color: "var(--warning)", marginBottom: "4px" }}
          >
            Cel miesieczny
          </div>

          {salesTarget.has_target ? (
            <>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "3px" }}>
                Cel: {salesTarget.target_amount.toLocaleString()} zl
              </div>

              {/* Pasek postepu */}
              <div style={{
                width: "100%",
                backgroundColor: "var(--border)",
                borderRadius: "4px",
                height: "5px",
                marginBottom: "3px"
              }}>
                <div style={{
                  width: `${Math.min(salesTarget.progress_percentage, 100)}%`,
                  backgroundColor: salesTarget.progress_percentage >= 100 ? "var(--success)" : "var(--warning)",
                  height: "100%",
                  borderRadius: "4px",
                  transition: "width 0.3s ease"
                }}></div>
              </div>

              <div style={{ fontSize: "10px", color: salesTarget.remaining_amount > 0 ? "var(--danger)" : "var(--success)" }}>
                {salesTarget.remaining_amount > 0
                  ? `Pozostalo: ${salesTarget.remaining_amount.toLocaleString()} zl`
                  : "Cel osiagniety!"
                }
              </div>
            </>
          ) : (
            <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>
              Brak celu
            </div>
          )}
        </div>

        {/* Premia miesieczna - pod celem */}
        <BonusWidget refreshTrigger={bonusRefreshTrigger} />
      </div>

      {/* D) Ostatnie wiadomosci */}
      <MessagesWidget userLogin={userLogin} locationId={locationId} />

      {/* E) Zadania na dzis */}
      <PosTasksBox userLogin={userLogin} locationId={locationId} />

    </div>
  );
};

export default memo(StatsBar);
